import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Alert, Badge, Button, Card, Form, InputGroup, Modal, Spinner } from 'react-bootstrap';
import {
  sprayService,
  SpraySession,
  SprayProduct,
  ProductConsumed,
} from '../../services/sprayService';
import {
  getSessionWithCacheFallback,
  getPendingSignOff,
  getProductsWithCacheFallback,
} from '../../services/spraySessionCacheService';
import { queueSignOff } from '../../services/spraySyncService';
import { useConnectivity } from '../../contexts/ConnectivityContext';
import {
  readDraft,
  writeDraft,
  clearDraft,
} from '../../services/sprayCompletionDraft';
import {
  getCachedWeather,
  refreshWeatherCache,
} from '../../services/weatherCacheService';
import {
  categorizeTemperature,
  categorizeWind,
  categorizePrecipitation,
  DEFAULT_THRESHOLDS,
  WeatherThresholds,
  TemperatureCategory,
  WindCategory,
  PrecipitationCategory,
} from '../../services/weatherCategorization';
import { getTenantInfo } from '../../services/tenantService';
import { WeatherCacheEntry } from '../../db';
import { formatTime } from '../../dateFormat';

/**
 * Format a Date as the value expected by an HTML <input type="datetime-local">,
 * which is "YYYY-MM-DDTHH:MM" in the user's local timezone.
 */
function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

const nowAsDatetimeLocal = (): string => toDatetimeLocalValue(new Date());

interface Props {
  sessionId: string;
}

interface CategoryRadioGroupProps {
  legend: string;
  ariaLabel: string;
  name: string;
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (next: string) => void;
  systemCategory: string | null;
  rawTooltip: string | null;
  t: (key: string, opts?: any) => string;
}

/**
 * One categorical-radio group (3 options: low / mid / high) with the
 * Verified vs Manual Override indicator next to its legend. Extracted so
 * temperature, wind, and precipitation share the exact same layout and
 * accessibility shape — fieldset + legend + named radios — and the form
 * body stays readable.
 */
const CategoryRadioGroup: React.FC<CategoryRadioGroupProps> = ({
  legend,
  ariaLabel,
  name,
  options,
  value,
  onChange,
  systemCategory,
  rawTooltip,
  t,
}) => {
  const overridden = Boolean(systemCategory && value && systemCategory !== value);
  const verified = Boolean(systemCategory && value && systemCategory === value);

  return (
    <fieldset className="mt-3" aria-label={ariaLabel}>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <legend className="form-label mb-0 fw-semibold">{legend}</legend>
        {verified && (
          <Badge bg="success" data-testid={`weather-status-${name}`}>
            <i className="bi bi-check-circle-fill me-1" aria-hidden="true" />
            {t('spray.completionForm.weatherVerified')}
          </Badge>
        )}
        {overridden && (
          <Badge bg="warning" text="dark" data-testid={`weather-status-${name}`}>
            <i className="bi bi-exclamation-triangle-fill me-1" aria-hidden="true" />
            {t('spray.completionForm.weatherOverride')}
          </Badge>
        )}
        {rawTooltip && (
          <span className="text-muted small" title={rawTooltip} data-testid={`${name}-raw`}>
            {rawTooltip}
          </span>
        )}
      </div>
      <div className="d-flex flex-wrap gap-3 mt-2">
        {options.map(opt => (
          <Form.Check
            key={opt.value}
            type="radio"
            id={`${name}-${opt.value}`}
            name={name}
            label={opt.label}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
          />
        ))}
      </div>
    </fieldset>
  );
};

/**
 * FieldModeCompletionForm — operator digital sign-off form mounted at
 * /spray/sessions/:id/complete *after* the session has been /start-ed.
 *
 * Phase 3 / Task 4 (track spray_session_workflow_20260427) refactor:
 *   - PPE was hoisted to the pre-flight screen (FieldModePreFlightForm) so
 *     it is enforced at /start, not /complete. This form no longer renders
 *     the checklist or includes ppeChecklist in its payload.
 *   - Both timestamps are editable datetime-local fields with sensible
 *     defaults: Start = session.startDate (the time logged at /start, which
 *     the operator may retroactively correct), End = "now". Both are
 *     clamped at max="now" so the input cannot future-date.
 *   - validate() mirrors the Phase 2 backend's merged-state checks: both
 *     timestamps required, parseable, ≤ now, and end strictly > start.
 *
 * Submission still flows through `sprayService.completeSession` online, with
 * `queueSignOff` (Dexie) as the offline / network-error fallback so a
 * sign-off captured on a flaky link is never lost.
 */
const FieldModeCompletionForm: React.FC<Props> = ({ sessionId }) => {
  const { t } = useTranslation();
  const { isOnline } = useConnectivity();
  const navigate = useNavigate();

  const [session, setSession] = useState<SpraySession | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);

  const [products, setProducts] = useState<SprayProduct[]>([]);

  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [windCategory, setWindCategory] = useState<WindCategory | ''>('');
  const [temperatureCategory, setTemperatureCategory] = useState<TemperatureCategory | ''>('');
  const [precipitationCategory, setPrecipitationCategory] = useState<PrecipitationCategory | ''>('');
  const [overrideComment, setOverrideComment] = useState('');
  const [quantities, setQuantities] = useState<Record<string, string>>({});

  // Cached weather + tenant thresholds power the auto-categorisation +
  // Verified/Override indicators. Both are loaded on mount; refreshing the
  // cache button below repopulates `cachedWeather` only.
  const [cachedWeather, setCachedWeather] = useState<WeatherCacheEntry | null>(null);
  const [thresholds, setThresholds] = useState<WeatherThresholds>(DEFAULT_THRESHOLDS);
  const [refreshing, setRefreshing] = useState(false);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Track: spray_weather_enhancements_20260429 / Phase 3.
  // Once a sign-off lands (online or offline-queue) the operator must
  // be redirected back to /spray/my-sessions per spec §2. The success
  // banner flashes briefly first so they see the confirmation before
  // the page swaps; the timer is cleared on unmount so a fast unmount
  // doesn't navigate after the component is gone.
  useEffect(() => {
    if (!successMessage) return;
    const id = setTimeout(() => {
      navigate('/protection/my-sessions', { replace: true });
    }, 800);
    return () => clearTimeout(id);
  }, [successMessage, navigate]);

  // True when a sign-off for this session is sitting in the offline
  // queue (pending / failed / syncing / quarantined). The form goes
  // read-only and pre-fills its state from the queued payload so the
  // operator sees what they actually submitted, not the editable
  // defaults. Cleared once the drainer either succeeds (cache + queue
  // entry both deleted) or hits a non-retryable failure (cache purged,
  // sync-error banner shown on My Sessions).
  const [isQueuedReadOnly, setIsQueuedReadOnly] = useState(false);

  // Track: spray_weather_enhancements_20260429 / Phase 4.
  // Original session.startDate as it was when the form mounted. Acts as
  // the baseline for "did the operator override the start time?". Stored
  // in a ref so a downstream re-render that changes setStartTime — for
  // example the load effect overlaying a saved draft — never marks the
  // initial seed as a user override.
  const originalStartDateRef = useRef<string | null>(null);

  // Prompt state for the start-time-override flow. Spec §1.1: when the
  // operator backdates the start time and the new time is within 48 h
  // of "now", ask whether to refresh the start-side weather snapshot
  // for that time. The prompt is purely about the weather refresh —
  // the start time itself is already updated.
  const [overridePrompt, setOverridePrompt] = useState<{
    pendingStartTime: string;
  } | null>(null);
  const [overrideRefreshing, setOverrideRefreshing] = useState(false);

  // Auto-save guard: skips the empty pre-hydrate render so we don't clobber
  // a saved draft before the load+overlay step finishes.
  const [hydrated, setHydrated] = useState(false);

  // End time tracks "now" until the operator manually overrides it. The
  // common Field Mode flow is "I'm finishing right now" — the auto-
  // refresh saves the operator from typing the time, and the auto-stop
  // respects retroactive corrections.
  const endTimeUserEdited = useRef(false);

  // Computed once on mount so it doesn't drift in tests. The backend's ≤ now
  // check is the source of truth; this is the input-level UX hint.
  const nowMax = useMemo(() => nowAsDatetimeLocal(), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    Promise.all([
      getSessionWithCacheFallback(sessionId),
      getPendingSignOff(sessionId).catch(() => null),
    ])
      .then(([{ session: loaded }, queuedSignOff]) => {
        if (cancelled) return;
        setSession(loaded);

        // Defaults: Start ← session.startDate (operator may retroactively
        // correct it), End ← "now" (operator confirms the moment they
        // walked off the orchard). A saved draft, if present, takes
        // precedence over these defaults.
        const startDefault = loaded.startDate
          ? toDatetimeLocalValue(new Date(loaded.startDate))
          : '';
        const endDefault = nowAsDatetimeLocal();

        // Snapshot the session's startDate as the baseline for the
        // start-time-override prompt (Phase 4 / Task 2). Comparing the
        // operator's edited value against the *original* keeps the
        // prompt from firing when the load effect itself overlays a
        // saved draft into setStartTime.
        if (originalStartDateRef.current === null) {
          originalStartDateRef.current = startDefault;
        }

        const seededQuantities: Record<string, string> = {};
        for (const pc of loaded.productsConsumed || []) {
          seededQuantities[pc.productId] =
            pc.plannedQuantity != null ? String(pc.plannedQuantity) : '';
        }

        if (queuedSignOff) {
          // A sign-off is queued for this session. Lock the form into
          // a read-only "Pending Sync" state pre-filled with the exact
          // payload the operator submitted, so they can verify what
          // will eventually land on the backend. Bypasses the draft
          // (which is cleared at queue time) and the auto-refresh
          // (which would mutate endTime under the operator's nose).
          const p = queuedSignOff.payload;
          const queuedQuantities: Record<string, string> = { ...seededQuantities };
          for (const pc of p.productsConsumed ?? []) {
            queuedQuantities[pc.productId] = String(pc.quantity);
          }
          setStartTime(p.startTime ?? startDefault);
          setEndTime(p.endTime ?? endDefault);
          // Legacy /complete payload carries the operator-chosen wind
          // category in the `wind` string field. Temperature and
          // precipitation categories are not in the legacy payload — the
          // auto-seed effect below will fall back to the cached system
          // reading for those radios in the read-only view.
          setWindCategory((p.wind as WindCategory | undefined) ?? '');
          setQuantities(queuedQuantities);
          setIsQueuedReadOnly(true);
          setHydrated(true);
          return;
        }

        const draft = readDraft(sessionId);
        // Neither timestamp is restored from the draft. Start defaults
        // to session.startDate (the moment recorded at /start, which is
        // the source of truth — the operator may retroactively correct
        // it but a stale draft override would resurrect a wrong value).
        // End defaults to "now" (the operator's flow is "I'm finishing
        // now"; reseeding on every mount avoids stale values). See the
        // endTimeUserEdited / auto-refresh effect below.
        setStartTime(startDefault);
        setEndTime(endDefault);
        setWindCategory((draft?.windCategory as WindCategory | undefined) ?? '');
        setTemperatureCategory(
          (draft?.temperatureCategory as TemperatureCategory | undefined) ?? ''
        );
        setPrecipitationCategory(
          (draft?.precipitationCategory as PrecipitationCategory | undefined) ?? ''
        );
        setOverrideComment(draft?.overrideComment ?? '');
        setQuantities(draft?.quantities ?? seededQuantities);
        setHydrated(true);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setLoadError(err?.message || 'Failed to load session.');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    let cancelled = false;
    // Cache-aware product catalog fetch: online → live /products + write
    // through to Dexie; offline / network error → read from the cached
    // catalog so productName + unit still resolve. Without this, raw
    // productIds leak into the offline sign-off review.
    getProductsWithCacheFallback()
      .then(list => {
        if (cancelled) return;
        setProducts(list || []);
      })
      .catch(() => {
        // Silent fallback — form still renders, product cells just
        // show the raw productId until the next online refresh.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const productMap = useMemo(() => {
    const map = new Map<string, SprayProduct>();
    for (const p of products) map.set(p._id, p);
    return map;
  }, [products]);

  // Load tenant thresholds + cached weather. Categorisation is the same
  // function the backend uses (see backend/src/services/sprayWeatherService),
  // ported to the frontend so the form can render Verified vs Manual Override
  // indicators without a network round-trip.
  useEffect(() => {
    let cancelled = false;

    getTenantInfo()
      .then(t => {
        if (cancelled) return;
        if (t?.weatherThresholds) {
          setThresholds(t.weatherThresholds as WeatherThresholds);
        }
      })
      .catch(() => {
        // Fall back to defaults if tenant fetch fails — the operator can
        // still complete the form, just with the platform default thresholds.
      });

    getCachedWeather()
      .then(entry => {
        if (cancelled) return;
        setCachedWeather(entry);
      })
      .catch(() => {
        // Empty cache is a valid state (offline, never synced) — nothing to do.
      });

    // Track: spray_weather_enhancements_20260429 / Phase 4.
    // Capture the end-of-session weather right now. The hourly cache row
    // can be up to 60 minutes stale, which is fine for a quick sign-off
    // but not for an audit-grade record of conditions at the moment the
    // operator stopped spraying. Calling capture-for-spray writes a fresh
    // WeatherHistory row server-side; refreshing the local cache afterward
    // pulls that row in so the auto-categorisation + Verified/Override
    // badges below run against the freshest reading.
    //
    // Reliability NFR: every failure path is silent. A 400
    // farm_location_not_set, a 5xx, or a network error still leaves the
    // form usable — the operator can fall back to cached values or pick
    // categories manually. Treating the call as best-effort keeps a
    // weather outage from blocking sign-off entirely.
    axios
      .post('/api/v1/tenants/me/weather/capture-for-spray', undefined, {
        withCredentials: true,
      })
      .then(() => refreshWeatherCache())
      .then(() => getCachedWeather())
      .then(entry => {
        if (cancelled) return;
        if (entry) setCachedWeather(entry);
      })
      .catch(() => {
        // Silent — see Reliability NFR comment above.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // System-reported categories computed from the cached raw reading + tenant
  // thresholds. Null when there is no cache to compare against — in which
  // case the operator must select categories manually (per spec, offline +
  // no cache).
  const systemCategory = useMemo(() => {
    if (!cachedWeather) return null;
    const { temp, windSpeed, precipitation } = cachedWeather;
    return {
      temperature:
        typeof temp === 'number' ? categorizeTemperature(temp, thresholds.temperature) : null,
      wind:
        typeof windSpeed === 'number' ? categorizeWind(windSpeed, thresholds.wind) : null,
      precipitation:
        typeof precipitation === 'number'
          ? categorizePrecipitation(precipitation, thresholds.precipitation)
          : null,
    };
  }, [cachedWeather, thresholds]);

  // Once we have the system reading and the operator has not yet committed a
  // value (no draft, no manual change), seed the radios from the system. This
  // is what implements "auto-fetched" pre-selection in the spec.
  useEffect(() => {
    if (!hydrated) return;
    if (!systemCategory) return;
    if (!windCategory && systemCategory.wind) setWindCategory(systemCategory.wind);
    if (!temperatureCategory && systemCategory.temperature) {
      setTemperatureCategory(systemCategory.temperature);
    }
    if (!precipitationCategory && systemCategory.precipitation) {
      setPrecipitationCategory(systemCategory.precipitation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, systemCategory]);

  const isOverridden = (system: string | null | undefined, user: string) =>
    Boolean(system && user && system !== user);

  const anyOverridden =
    isOverridden(systemCategory?.temperature, temperatureCategory) ||
    isOverridden(systemCategory?.wind, windCategory) ||
    isOverridden(systemCategory?.precipitation, precipitationCategory);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshWeatherCache();
      const entry = await getCachedWeather();
      setCachedWeather(entry);
    } finally {
      setRefreshing(false);
    }
  };

  // Track: spray_weather_enhancements_20260429 / Phase 4.
  // 48 h history window, in milliseconds. Mirrors the backend's
  // /me/weather/recent cap and WeatherAPI's history-endpoint horizon.
  const HISTORY_WINDOW_MS = 48 * 60 * 60 * 1000;

  /**
   * Decide whether the start-time edit should trigger the
   * override-weather prompt:
   *   - The new value differs from the *original* session.startDate
   *     (not from the current draft, so a load-time draft overlay
   *     does not count as an override).
   *   - The new value parses to a real date.
   *   - That date is within the last 48 h of "now". Outside that
   *     window the WeatherAPI history endpoint cannot help, so the
   *     prompt would be misleading.
   */
  const shouldPromptForWeatherRefresh = (newValue: string): boolean => {
    if (!newValue) return false;
    const original = originalStartDateRef.current;
    if (!original) return false;
    if (newValue === original) return false;
    const newMs = Date.parse(newValue);
    if (Number.isNaN(newMs)) return false;
    const now = Date.now();
    const ageMs = now - newMs;
    if (ageMs < 0 || ageMs > HISTORY_WINDOW_MS) return false;
    return true;
  };

  /**
   * Wrap the startTime input's onChange so we can layer the
   * override-weather prompt on top of the underlying setStartTime.
   * The start time itself is updated unconditionally — the prompt is
   * only about whether to refresh the start-side weather snapshot.
   */
  const handleStartTimeChange = (newValue: string) => {
    setStartTime(newValue);
    if (shouldPromptForWeatherRefresh(newValue)) {
      setOverridePrompt({ pendingStartTime: newValue });
    }
  };

  /**
   * Yes path on the override prompt. Pull /weather/recent?hours=48,
   * find the row whose timestamp is closest to the operator's new
   * start time, and write its categorisation to the per-session draft
   * under the start*Category keys. The auto-save effect would
   * otherwise rewrite those keys on the next field touch — so we
   * merge through the existing draft just like the close-side
   * writeDraft does.
   */
  const confirmStartWeatherRefresh = async () => {
    if (!overridePrompt) return;
    const targetMs = Date.parse(overridePrompt.pendingStartTime);
    if (Number.isNaN(targetMs)) {
      setOverridePrompt(null);
      return;
    }
    setOverrideRefreshing(true);
    try {
      const res = await axios.get<{
        entries: Array<{
          timestamp: string;
          temp?: number;
          windSpeed?: number;
          precipitation?: number;
        }>;
      }>('/api/v1/tenants/me/weather/recent', {
        params: { hours: 48 },
        withCredentials: true,
      });
      const entries = res.data?.entries || [];
      if (entries.length === 0) {
        setOverridePrompt(null);
        return;
      }
      // Closest-by-timestamp wins. Ties favour the earlier row, which
      // doesn't matter in practice — WeatherHistory is hourly.
      const closest = entries.reduce((best, cur) => {
        const bestDelta = Math.abs(Date.parse(best.timestamp) - targetMs);
        const curDelta = Math.abs(Date.parse(cur.timestamp) - targetMs);
        return curDelta < bestDelta ? cur : best;
      });

      const startTemp =
        typeof closest.temp === 'number'
          ? categorizeTemperature(closest.temp, thresholds.temperature)
          : undefined;
      const startWind =
        typeof closest.windSpeed === 'number'
          ? categorizeWind(closest.windSpeed, thresholds.wind)
          : undefined;
      const startPrecip =
        typeof closest.precipitation === 'number'
          ? categorizePrecipitation(closest.precipitation, thresholds.precipitation)
          : undefined;

      const existing = readDraft(sessionId);
      writeDraft(sessionId, {
        ...(existing ?? {}),
        startTemperatureCategory: startTemp,
        startWindCategory: startWind,
        startPrecipitationCategory: startPrecip,
      });
    } catch {
      // Reliability NFR: a failed weather refresh must not block the
      // override flow. The start time is already updated; the operator
      // can still pick categories manually.
    } finally {
      setOverrideRefreshing(false);
      setOverridePrompt(null);
    }
  };

  const dismissStartWeatherRefresh = () => setOverridePrompt(null);

  // Auto-save: every change to a form field rewrites the draft. Cheap —
  // localStorage writes are synchronous and the payload is tiny — so we do
  // it eagerly without debouncing. The hydrated guard prevents the empty
  // initial render from clobbering an existing draft before it loads.
  useEffect(() => {
    if (!hydrated || isQueuedReadOnly) return;
    // Carry over fields owned by adjacent surfaces:
    //   - PPE is owned by the pre-flight form (Phase 3 hoist).
    //   - start*Category fields are owned by the open sign-off panel
    //     (spray_weather_enhancements_20260429 / Phase 2).
    // Without this carry-over the close sign-off's writeDraft would
    // wipe the open sign-off's saved start-side weather selections the
    // first time the operator edits anything here.
    const existing = readDraft(sessionId);
    writeDraft(sessionId, {
      ppe: existing?.ppe,
      startTime,
      endTime,
      windCategory: windCategory || undefined,
      temperatureCategory: temperatureCategory || undefined,
      precipitationCategory: precipitationCategory || undefined,
      startWindCategory: existing?.startWindCategory,
      startTemperatureCategory: existing?.startTemperatureCategory,
      startPrecipitationCategory: existing?.startPrecipitationCategory,
      // Track: spray_weather_enhancements_20260429 / Phase 5 wiring fix.
      // Carry the open sign-off panel's raw reading through so the
      // /complete payload's weatherSnapshots.start.systemReported is
      // populated. Without this carry-forward the close-side autosave
      // would wipe the field on the operator's first edit.
      startWeatherReading: existing?.startWeatherReading,
      overrideComment: overrideComment || undefined,
      quantities,
    });
  }, [
    hydrated,
    isQueuedReadOnly,
    sessionId,
    startTime,
    endTime,
    windCategory,
    temperatureCategory,
    precipitationCategory,
    overrideComment,
    quantities,
  ]);

  // End time auto-refresh: while the operator has the form open and
  // has not manually edited the End time, keep the field current so
  // a quick tap on Submit always logs the actual finish moment.
  // Stops the moment the operator types in the field — retroactive
  // corrections must stick. Disabled when the form is locked into
  // the queued / read-only render: the displayed endTime is what the
  // operator actually submitted and should not silently mutate.
  useEffect(() => {
    if (!hydrated || isQueuedReadOnly) return;
    const id = setInterval(() => {
      if (endTimeUserEdited.current) return;
      setEndTime(nowAsDatetimeLocal());
    }, 30 * 1000);
    return () => clearInterval(id);
  }, [hydrated, isQueuedReadOnly]);

  const setQuantity = (productId: string, value: string) => {
    setQuantities(prev => ({ ...prev, [productId]: value }));
  };

  const validate = (): string | null => {
    if (!startTime) return t('spray.completionForm.startTimeRequired');
    if (!endTime) return t('spray.completionForm.endTimeRequired');
    const startMs = Date.parse(startTime);
    const endMs = Date.parse(endTime);
    if (Number.isNaN(startMs)) return t('spray.completionForm.startTimeInvalid');
    if (Number.isNaN(endMs)) return t('spray.completionForm.endTimeInvalid');
    const nowMs = Date.now();
    if (startMs > nowMs) return t('spray.completionForm.startTimeInFuture');
    if (endMs > nowMs) return t('spray.completionForm.endTimeInFuture');
    if (endMs <= startMs) return t('spray.completionForm.endBeforeStart');
    if (!windCategory) return t('spray.completionForm.windRequired');
    if (!temperatureCategory) return t('spray.completionForm.temperatureRequired');
    if (!precipitationCategory) return t('spray.completionForm.precipitationRequired');
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSuccessMessage('');

    const validationError = validate();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    const productsConsumed: ProductConsumed[] = Object.entries(quantities).map(
      ([productId, q]) => ({ productId, quantity: Number(q) || 0 })
    );

    // Track: spray_weather_enhancements_20260429 / Phase 5 wiring fix.
    // Build the audit-grade weatherSnapshots payload alongside the legacy
    // wind/temperature fields. The backend persists snapshots when
    // present and skips the legacy writes (whose deprecated validators
    // reject saves where snapshots are also being written).
    //   - end side: cachedWeather + this form's three radios
    //   - start side: pulled from the per-session draft, where the open
    //     sign-off panel persisted the operator's earlier selections +
    //     the raw reading it auto-categorised against.
    const draftAtSubmit = readDraft(sessionId);
    const startSnap = (draftAtSubmit?.startTemperatureCategory ||
      draftAtSubmit?.startWindCategory ||
      draftAtSubmit?.startPrecipitationCategory ||
      draftAtSubmit?.startWeatherReading)
      ? {
          timestamp:
            draftAtSubmit?.startWeatherReading?.timestamp ||
            (startTime ? new Date(startTime).toISOString() : undefined),
          systemReported:
            draftAtSubmit?.startWeatherReading
              ? {
                  temp: draftAtSubmit.startWeatherReading.temp,
                  windSpeed: draftAtSubmit.startWeatherReading.windSpeed,
                  precipitation: draftAtSubmit.startWeatherReading.precipitation,
                }
              : undefined,
          finalSelected: {
            temperature: draftAtSubmit?.startTemperatureCategory || undefined,
            wind: draftAtSubmit?.startWindCategory || undefined,
            precipitation: draftAtSubmit?.startPrecipitationCategory || undefined,
          },
        }
      : undefined;

    const endSnap = (windCategory || temperatureCategory || precipitationCategory || cachedWeather)
      ? {
          timestamp: endTime ? new Date(endTime).toISOString() : undefined,
          systemReported:
            cachedWeather
              ? {
                  temp: cachedWeather.temp,
                  windSpeed: cachedWeather.windSpeed,
                  precipitation: cachedWeather.precipitation,
                }
              : undefined,
          finalSelected: {
            temperature: temperatureCategory || undefined,
            wind: windCategory || undefined,
            precipitation: precipitationCategory || undefined,
          },
        }
      : undefined;

    const weatherSnapshots = (startSnap || endSnap)
      ? { start: startSnap, end: endSnap }
      : undefined;

    // Legacy /complete contract: wind=string, temperature=number. The
    // backend prefers weatherSnapshots when present and ignores these,
    // but we keep them so legacy listeners (older queued sign-offs in
    // the offline drainer, BC-test fixtures) still see something.
    const payload = {
      startTime,
      endTime,
      wind: windCategory as string,
      temperature: typeof cachedWeather?.temp === 'number' ? cachedWeather.temp : 0,
      productsConsumed,
      ...(weatherSnapshots ? { weatherSnapshots } : {}),
    };

    setSubmitting(true);

    if (!isOnline) {
      try {
        await queueSignOff(sessionId, payload);
        clearDraft(sessionId);
        setSuccessMessage(t('spray.completionForm.queuedOffline'));
        // Lock the form so a second tap on Submit can't queue a
        // duplicate sign-off. The drainer would otherwise process both
        // and the second /complete would 400 ('alreadyCompleted'),
        // surfacing as a false sync-error banner.
        setIsQueuedReadOnly(true);
      } catch (err: any) {
        setFormError(err?.message || t('spray.completionForm.queueError'));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    try {
      await sprayService.completeSession(sessionId, payload);
      clearDraft(sessionId);
      setSuccessMessage(t('spray.completionForm.submitSuccess'));
    } catch (err: any) {
      const hasBackendResponse = !!(err && (err.cause?.response || err.response));
      if (!hasBackendResponse) {
        try {
          await queueSignOff(sessionId, payload, err?.message);
          clearDraft(sessionId);
          setSuccessMessage(t('spray.completionForm.queuedOffline'));
          // Same lock as the pure-offline path — the form is now in
          // a "queued, awaiting sync" state and must not accept
          // another submission.
          setIsQueuedReadOnly(true);
        } catch (queueErr: any) {
          setFormError(queueErr?.message || t('spray.completionForm.queueError'));
        }
      } else {
        setFormError(err?.message || t('spray.completionForm.submitError'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="field-mode-completion-form text-center p-4">
        <Spinner animation="border" role="status" />
        <div className="mt-2">{t('common.loading')}</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="field-mode-completion-form p-3">
        <Alert variant="danger">{loadError}</Alert>
      </div>
    );
  }

  if (!session) return null;

  return (
    <Form
      className="field-mode-completion-form"
      onSubmit={handleSubmit}
      noValidate
    >
      {isQueuedReadOnly && (
        // Operator-facing explanation of why everything is locked. The
        // banner stays until either the drainer succeeds (cache + queue
        // entry purged → component remounts onto a refreshed list) or a
        // non-retryable failure converts the row into a sync-error
        // banner on My Sessions.
        <Alert variant="warning" className="mb-3">
          {t('spray.completionForm.queuedSyncBanner')}
        </Alert>
      )}

      {/* Timing */}
      <Card className="mb-3">
        <Card.Body>
          <Card.Title>{t('spray.completionForm.timingSection')}</Card.Title>
          <div className="d-flex flex-column flex-md-row gap-3 mt-2">
            <Form.Group className="flex-grow-1" controlId="fm-start-time">
              <Form.Label>{t('spray.completionForm.startTime')}</Form.Label>
              <Form.Control
                type="datetime-local"
                value={startTime}
                max={nowMax}
                onChange={e => handleStartTimeChange(e.target.value)}
                disabled={isQueuedReadOnly}
              />
            </Form.Group>
            <Form.Group className="flex-grow-1" controlId="fm-end-time">
              <Form.Label>{t('spray.completionForm.endTime')}</Form.Label>
              <Form.Control
                type="datetime-local"
                value={endTime}
                max={nowMax}
                onChange={e => {
                  // Mark as user-overridden so the auto-refresh stops.
                  endTimeUserEdited.current = true;
                  setEndTime(e.target.value);
                }}
                disabled={isQueuedReadOnly}
              />
            </Form.Group>
          </div>
        </Card.Body>
      </Card>

      {/* Weather */}
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center">
            <Card.Title className="mb-0">
              {t('spray.completionForm.weatherSection')}
            </Card.Title>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              type="button"
            >
              {refreshing
                ? t('spray.completionForm.weatherRefreshing')
                : t('spray.completionForm.weatherRefresh')}
            </Button>
          </div>

          {!cachedWeather && (
            <Alert variant="warning" className="mt-2 mb-0 py-2">
              {t('spray.completionForm.weatherNoCache')}
            </Alert>
          )}

          {cachedWeather && (
            <div className="text-muted small mt-2" data-testid="weather-cached-at">
              {t('spray.completionForm.weatherCachedAt', {
                time: formatTime(cachedWeather.timestamp),
              })}
            </div>
          )}

          {/* Temperature category radios */}
          <CategoryRadioGroup
            legend={t('spray.completionForm.temperature')}
            ariaLabel={t('spray.completionForm.temperature')}
            name="fm-temperature"
            options={[
              { value: 'Cold', label: t('spray.completionForm.temperatureCold') },
              { value: 'Optimal', label: t('spray.completionForm.temperatureOptimal') },
              { value: 'Hot', label: t('spray.completionForm.temperatureHot') },
            ]}
            value={temperatureCategory}
            onChange={v => setTemperatureCategory(v as TemperatureCategory)}
            systemCategory={systemCategory?.temperature ?? null}
            rawTooltip={
              typeof cachedWeather?.temp === 'number'
                ? t('spray.completionForm.weatherRawTemp', { value: cachedWeather.temp })
                : null
            }
            t={t}
          />

          {/* Wind category radios */}
          <CategoryRadioGroup
            legend={t('spray.completionForm.wind')}
            ariaLabel={t('spray.completionForm.wind')}
            name="fm-wind"
            options={[
              { value: 'Calm', label: t('spray.completionForm.windCalm') },
              { value: 'Breeze', label: t('spray.completionForm.windBreeze') },
              { value: 'Strong', label: t('spray.completionForm.windStrong') },
            ]}
            value={windCategory}
            onChange={v => setWindCategory(v as WindCategory)}
            systemCategory={systemCategory?.wind ?? null}
            rawTooltip={
              typeof cachedWeather?.windSpeed === 'number'
                ? t('spray.completionForm.weatherRawWind', { value: cachedWeather.windSpeed })
                : null
            }
            t={t}
          />

          {/* Precipitation category radios */}
          <CategoryRadioGroup
            legend={t('spray.completionForm.precipitation')}
            ariaLabel={t('spray.completionForm.precipitation')}
            name="fm-precipitation"
            options={[
              { value: 'None', label: t('spray.completionForm.precipitationNone') },
              { value: 'Light', label: t('spray.completionForm.precipitationLight') },
              { value: 'Heavy', label: t('spray.completionForm.precipitationHeavy') },
            ]}
            value={precipitationCategory}
            onChange={v => setPrecipitationCategory(v as PrecipitationCategory)}
            systemCategory={systemCategory?.precipitation ?? null}
            rawTooltip={
              typeof cachedWeather?.precipitation === 'number'
                ? t('spray.completionForm.weatherRawPrecip', {
                    value: cachedWeather.precipitation,
                  })
                : null
            }
            t={t}
          />

          {anyOverridden && (
            <Form.Group className="mt-3" controlId="fm-override-comment">
              <Form.Label>{t('spray.completionForm.overrideCommentLabel')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder={t('spray.completionForm.overrideCommentPlaceholder') as string}
                value={overrideComment}
                onChange={e => setOverrideComment(e.target.value)}
              />
            </Form.Group>
          )}
        </Card.Body>
      </Card>

      {/* Product quantities */}
      {session.productsConsumed && session.productsConsumed.length > 0 && (
        <Card className="mb-3">
          <Card.Body>
            <Card.Title>{t('spray.completionForm.quantitiesSection')}</Card.Title>
            <div className="d-flex flex-column gap-2 mt-2">
              {session.productsConsumed.map(pc => {
                const inputId = `fm-qty-${pc.productId}`;
                const product = productMap.get(pc.productId);
                const productName = product?.productName ?? pc.productId;
                const unit = product?.dosePer100LUnit;
                return (
                  <Form.Group
                    key={pc.productId}
                    controlId={inputId}
                    className="d-flex align-items-center gap-2"
                  >
                    <Form.Label className="mb-0 flex-grow-1">
                      {productName}
                      {pc.plannedQuantity != null && (
                        <span className="text-muted ms-2">
                          ({t('spray.completionForm.planned')}: {pc.plannedQuantity}
                          {unit ? ` ${unit}` : ''})
                        </span>
                      )}
                    </Form.Label>
                    <InputGroup style={{ maxWidth: 200 }}>
                      <Form.Control
                        type="number"
                        step="0.01"
                        aria-label={productName}
                        value={quantities[pc.productId] ?? ''}
                        onChange={e => setQuantity(pc.productId, e.target.value)}
                        disabled={isQueuedReadOnly}
                      />
                      {unit && <InputGroup.Text>{unit}</InputGroup.Text>}
                    </InputGroup>
                  </Form.Group>
                );
              })}
            </div>
          </Card.Body>
        </Card>
      )}

      {formError && <Alert variant="danger">{formError}</Alert>}
      {/* Suppress the success alert when the read-only banner is up — both
          would carry overlapping "saved offline" copy and one of them is
          enough. The banner stays; the success alert is for the online
          path. */}
      {successMessage && !isQueuedReadOnly && <Alert variant="success">{successMessage}</Alert>}

      <div className="d-grid">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={submitting || isQueuedReadOnly}
        >
          {isQueuedReadOnly
            ? t('spray.completionForm.queuedSyncButton')
            : submitting
              ? t('spray.completionForm.submitting')
              : t('spray.completionForm.submitButton')}
        </Button>
      </div>

      {/* Phase 4 / Task 2: start-time-override weather refresh prompt. */}
      <Modal
        show={overridePrompt !== null}
        onHide={dismissStartWeatherRefresh}
        centered
        data-testid="start-time-override-prompt"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t('spray.completionForm.startOverride.title')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('spray.completionForm.startOverride.body')}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={dismissStartWeatherRefresh}
            disabled={overrideRefreshing}
          >
            {t('spray.completionForm.startOverride.dismiss')}
          </Button>
          <Button
            variant="primary"
            onClick={confirmStartWeatherRefresh}
            disabled={overrideRefreshing}
          >
            {overrideRefreshing
              ? t('spray.completionForm.startOverride.refreshing')
              : t('spray.completionForm.startOverride.confirm')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Form>
  );
};

export default FieldModeCompletionForm;
